import { OpaccAutoCompleter } from '../../lib/autocomplete/opacc.lib.autocompleter.js';
import { autocompleteMessages } from '../messages/opacc.messages.js';
import { opaccAjaxPlugin } from '../opacc.static.js';

/** @module form */

/**
 * Funktionalität zur Autovervollständigung bei Listenabfragen
 * @param {Object} [customSettings=null] Benutzerdefinierte Einstellungen um die Autovervollständigung anzupassen
 */
export class OpaccFormElementAutoComplete {

	constructor(customSettings = null) {
		// default Wert für die Thresholds, wenn in gGetBoAttrField kein Wert für Threshold gesetzt wurde
		this.defaultThreshold = 3;
		this.wrapperElement = document.querySelector('.content');
		this.autocompleteWrapperSelector = '.opc-autocomplete';
		this.autocompleteFields = this.wrapperElement.querySelectorAll(`${this.autocompleteWrapperSelector}`);

		Array.from(this.autocompleteFields).forEach(item => {
			this.settings = null;
			let inputField = item.querySelector('[data-op-type="autocomplete"]');
			let hiddenField = inputField.parentElement.querySelector('input[type="hidden"]');

			// start-loading animation
			item.classList.add('is-loading');

			// check for Async-Data
			const dataLoaded = this.loadData(item, hiddenField, inputField);
			dataLoaded.then(result => {
				if (result === true) {
					item.classList.remove('is-loading');
				}
			});

			//default properties
			const defaultSettings = {
				minLength: item.dataset.opThreshold || this.defaultThreshold,
				input: inputField,
				onSelect: this.onSelectFnc.bind(this, hiddenField),
				showOnFocus: item.dataset.opAutoload ? item.dataset.opAutoload.toLowerCase() === 'true' : true,
				fetch: this.filterList.bind(this, hiddenField, inputField, item),
				render: this.renderList.bind(this, item),
				emptyMsg: autocompleteMessages.messageNoValueFound.text,
				renderGroup: this.renderGroup.bind(this),
				container: item.querySelector('.form-field-list > ul.select-list')
			};

			if (customSettings) {
				if (typeof customSettings != 'object') {
					throw new Error ('inputparameter must be an Object!');
				}
			}

			this.settings = Object.assign(defaultSettings, customSettings);
			this.attachEventListener(inputField, hiddenField);
			this.autocomplete = new OpaccAutoCompleter(this.settings);
		});
	}

	/**
	 * Diese Methode wird der Autocomplete-Library übergeben und dient zur Aufbereitung der Autocomplete-Liste.
	 *
	 * @param {HTMLElement} hiddenField Verstecktes Formularfeld, dient der Speicherung des ausgewählten Wertes
	 * @param {HTMLElement} inputField Angezeigtes Formularfeld, dient der Anzeige des ausgewählten Labels
	 * @param {HTMLElement} el Autocomplete Element
	 * @param {string} text Suchstring
	 * @param {Function} update Vorschläge des Autocomplete-Feldes aktualisieren
	 */
	filterList(hiddenField, inputField, el, text, update) {
		// add loading-animation
		el.classList.add('is-loading');

		let match = text.toLowerCase();
		let dataLoaded = this.getAsynData(this.prepareAsyncUrl(inputField, el));
		dataLoaded.then((result) => {
			let suggestions = null;
			if ('opExtendedSearch' in inputField.dataset && inputField.dataset.opExtendedSearch.toLowerCase() === 'true') {
				suggestions = result;
			} else {
				suggestions = result.filter(item => {
					let label = item.label;
					return label.toLowerCase().indexOf(match) !== -1;
				});

			}
			update(suggestions);
			if (inputField.defaultValue != inputField.value) {
				this.clearInputFieldSelection(hiddenField);
			}

			// remove loading-animation
			el.classList.remove('is-loading');

		}).catch(() => {throw new Error ('Filtered list could not be loaded');});
	}

	/**
	 * Diese Methode wird der Autocomplete-Library übergeben. Beim auswählen eines Items aus der Liste wird diese Methode ausgeführt.
	 *
	 * @param {HTMLElement} hiddenField verstecktes Formularfeld, dient der Speicherung des ausgewählten Wertes
	 * @param {Object} item Ausgewähltes Element aus der Liste
	 * @param {HTMLElement} inputField angezeigtes Formularfeld, dient der Anzeige des ausgewählten Labels
	 */
	onSelectFnc(hiddenField, item, inputField) {
		hiddenField.value = item.value;
		inputField.value = item.label;
		inputField.defaultValue = inputField.value;

		if (this.settings.onSelectHook) {
			this.settings.onSelectHook(item, inputField);
		}
	}

	/**
	 * Diese Methode wird der Autocomplete-Library übergeben und legt das Rendering der Rückgabe-Liste fest. Für jeden Autocomplete-Vorschlag wird diese Methode 1 x ausgeführt.
	 *
	 * @param {HTMLElement} el Autocomplete Element
	 * @param {Object} item Einzelnes Element aus der Resultats-Liste
	 * @param {string} currentValue Aktueller Suchstring
	 * @returns Formatierter Autocomplete-Vorschlag
	 */
	renderList(el, item, currentValue) {
		let element = document.createElement('li');
		let highLightMatch = el.dataset.opHighlight.toLowerCase() === 'true';
		this.returnElementMarkup(element, item, highLightMatch, currentValue);
		return element;
	}

	/**
	 *  Diese Methode wird der Autocomplete-Library übergeben und legt das Rendering für eine allfällige Gruppierung der Rückgabe-Elemente fest. Für Jede Gruppe wird diese Methode 1 x ausgeführt.
	 *
	 * @param {string} groupName Anzuzeigender Gruppenname
	 * @param {string} currentValue Aktueller Suchstring
	 * @returns Formatierter Gruppenname als Listenelement für Autocomplete-Ergebnis
	 * @memberof OpaccFormElementAutoComplete
	 */
	// eslint-disable-next-line no-unused-vars
	renderGroup(groupName, currentValue) {
		let element = document.createElement('li');
		element.textContent = groupName;
		return element;
	}

	/***************************************
	 * Opacc-Specific autocomplete-Methods *
	 ***************************************/

	/**
	 * Event Registrierungen im DOM
	 *
	 * @param {HTMLElement} inputField angezeigtes Formularfeld, dient der Anzeige des ausgewählten Labels
	 * @param {HTMLElement} hiddenField verstecktes Formularfeld, dient der Speicherung des ausgewählten Wertes
	 */
	attachEventListener(inputField, hiddenField) {
		inputField.addEventListener('blur', (e) => {this.blurEventHandler(inputField, hiddenField, e);});
	}

	/**
	 * Handler des Blur-Events. Dieser Handler wird durch den Event "blur" ausgeführt.
	 *
	 * @param {HTMLElement} inputField angezeigtes Formularfeld, dient der Anzeige des ausgewählten Labels
	 * @param {HTMLElement} hiddenField verstecktes Formularfeld, dient der Speicherung des ausgewählten Wertes
	 * @param {Event} e Event, der durch den Listener ausgelöst wurde
	 */
	blurEventHandler(inputField, hiddenField, e) {
		e.preventDefault();
		if (hiddenField.value != '') {
			return;
		}
		this.clearInputFieldSelection(inputField);
		this.clearInputFieldSelection(hiddenField);
	}

	/**
	 * Löschen des value Attributs auf dem Input-Element.
	 *
	 * @param {HTMLElement} element angezeigtes Formularfeld zum Löschen des Wertes
	 */
	clearInputFieldSelection(element) {
		element.value = '';
		element.defaultValue = '';
	}

	/**
	 * Rückgabe der aufbereiteten HTML-Elemente (Bild, Beschreibung, Label).
	 *
	 * @param {HTMLElement} el "li" Tag zur Anreicherung der Attribute
	 * @param {Object} item Einzelner Autocomplete-Vorschlag
	 * @param {boolean } highLightMatch Bestimmt, ob der Suchtext in der Ergebnis-Liste hervorgehoben werden soll
	 * @param {string} currentVal Aktueller Suchstring
	 * @returns Formatiertes "li" Tag
	 */
	returnElementMarkup(el, item, highLightMatch, currentVal) {
		let returnEl = el;
		returnEl.dataset.opValue = item.value;
		returnEl.setAttribute('tabindex', '-1');

		let imageTag = this.createImageTag(item);
		let descriptionTag = this.createDescriptionTag(item);
		let labelInnerHTML = this.createLabelTag(item);
		let wrapperTag = this.createWrapperTag(item);

		if (highLightMatch) {
			let regex = new RegExp(currentVal, 'gi');
			let inner = item.label.replace(regex, match => { return this.highlightMarkup(match);});
			labelInnerHTML.innerHTML = inner;
		}

		returnEl.append(imageTag);
		returnEl.append(wrapperTag);
		if (wrapperTag) {
			wrapperTag.append(labelInnerHTML);
			wrapperTag.append(descriptionTag);
		} else {
			returnEl.append(labelInnerHTML);
			returnEl.append(descriptionTag);
		}
		return returnEl;
	}

	/**
	 * Rückgabe des Suchstring im Wrapper-Markup zur farblichen Kennzeichnug innerhalb des Resultates.
	 *
	 * @param {string} match Suchstring
	 * @returns Formatiertes HTML-Element zur farblichen Kennzeichnung innerhalb des Resultats.
	 */
	highlightMarkup(match) {
		return `<span class='autocomplete-highlight'>${match}</span>`;
	}

	/**
	 * Erstellung des Wrapper-Elementes für Titel und Beschreibung.
	 *
	 * @param {Object} item Einzelner Autocomplete-Vorschlag
	 * @returns Formatierter Wrapper Tag für Titel und Beschreibung
	 */
	createWrapperTag(item) {
		if (!item.image && !item.description) {
			return '';
		}

		let wrapperTag = document.createElement('div');
		wrapperTag.setAttribute('class', 'list-item-wrapper');
		return wrapperTag;
	}

	/**
	 * Erstellung des Bild-Elementes
	 *
	 * @param {Object} item Einzelner Autocomplete-Vorschlag
	 * @returns Rückgabe des aufbereiteten Bild-Elementes.
	 */
	createImageTag(item) {
		if (!item.image) {
			return '';
		}

		let imageTag = document.createElement('img');
		imageTag.setAttribute('class','list-item-image');
		imageTag.src = item.image;
		imageTag.alt = item.label;
		return imageTag;
	}

	/**
	 * Erstellung der Beschreibung für das Autocomplete-Element
	 *
	 * @param {Object} item Einzelner Autocomplete-Vorschlag
	 * @returns Rückgabe des aufbereiteten Beschreibung-Elementes.
	 */
	createDescriptionTag(item) {
		if (!item.description) {
			return '';
		}

		let descriptionTag = document.createElement('span');
		descriptionTag.setAttribute('class','list-item-description');
		descriptionTag.innerHTML = item.description;
		return descriptionTag;
	}

	/**
	 * Erstellung des Label-Elementes
	 *
	 * @param {Object} item Einzelner Autocomplete-Vorschlag
	 * @returns Rückgabe des aufbereiteten Label-Elementes.
	 */
	createLabelTag(item) {
		let labelTag = document.createElement('span');
		labelTag.setAttribute('class','list-item-name');
		labelTag.innerHTML = item.label;
		return labelTag;
	}

	/**
	 * Ermittlung des Defaultwertes, falls ein Defaultwert definiert wurde.
	 *
	 * @param {Object} item Einzelner Autocomplete-Vorschlag
	 * @param {string} initValue Default-Wert zur Initialen Anzeige eines Resultates
	 * @param {HTMLElement} inputField Angezeigtes Formularfeld, dient der Anzeige des ausgewählten Labels
	 * @returns Promise zur Weiterverarbeitung der asynchronen Operation
	 */
	getByInitValue(item, initValue, inputField) {
		return new Promise((resolve, reject) => {
			let initialLoad = this.getAsynData(this.prepareAsyncUrl(inputField, item));
			initialLoad.then((result) => {
				let selection = result.filter(item => {
					return item.value === initValue;
				});
				let retVal = '';
				if (selection.length > 0) {
					retVal = selection[0].label;
				}
				resolve(retVal);
			}).catch(error => {
				reject(error);
			});
		});
	}

	/**
	 * Aufbereitung der asynchronen URL
	 *
	 * @param {HTMLElement} inputField Formularfeld für die Autocomplete-Suche
	 * @param {HTMLElement} item Autocomplete-Element
	 * @returns URL für die asynchrone Abfrage
	 */
	prepareAsyncUrl(inputField, item) {
        let url = opaccAjaxPlugin.setAsyncUrl(true);

		let asyncUrl = item.dataset.opUrl || `${url}?type=${inputField.dataset.opType}&crypt=${inputField.dataset.opCrypt}`;
		let separator = asyncUrl.indexOf('?') > 0 ? '&' : '?';
		let valueParam = `value=${encodeURI(inputField.value)}`;
		let selectedParam = '';
		let inputs = item.getElementsByTagName('input');
		if(inputs.length > 0 && inputs[0].value)
		{
			selectedParam = `&selected=${encodeURI(inputs[0].value)}`;
		}
		return `${asyncUrl}${separator}${valueParam}${selectedParam}`;
	}

	/**
	 * Aufruf des asynchronen Requests.
	 *
	 * @param {string} url Ziel-URL als String
	 * @returns Promise zur Weiterverarbeitung der asynchronen Operation
	 */
	getAsynData(url) {
		return new Promise(resolve => {
			opaccAjaxPlugin.fetchAsynGetJson(url, this.getResultList.bind(this, resolve), this.asyncErrorHandler.bind(this), true );
		});
	}


	/**
	 * Freigabe (resolve) des asynchronen Requests (Promise) zur Weiterverarbeitung des Resultats.
	 *
	 * @param {Function} resolve Funktion um das ausstehende Resultat der Promise freizugeben
	 * @param {Object} response Ergebnisobjekt mit dem Resultat des asynchronen Requests.
	 */
	getResultList(resolve, response) {
		if (response.success) {
			let autocompleteList = response.dataAsJson;
			resolve(autocompleteList);
		}
	}

	/**
	 * Ermittlung des Status des Autocomplete-Feldes.
	 *
	 * @param {Object} item Einzelner Autocomplete-Vorschlag
	 * @param {HTMLElement} hiddenField Verstecktes Formularfeld, dient der Speicherung des ausgewählten Wertes
	 * @param {HTMLElement} inputField Angezeigtes Formularfeld, dient der Anzeige des ausgewählten Labels
	 * @returns Promise mit Status des Ladevorgangs
	 */
	loadData(item, hiddenField, inputField) {
		return new Promise((resolve, reject) => {
			if (hiddenField && hiddenField.value) {
				let initData = this.getByInitValue(item, hiddenField.value, inputField);
				initData.then((result) => {
					inputField.value = result;
					inputField.defaultValue = inputField.value;

					resolve(true);
				}).catch(error => {
					reject(error);
				});
			} else {
				resolve(true);
			}
		});
	}

	/**
	 * Methode für fehlerhaften Ajax-Request
	 *
	 * @param {DOMException} error Error-Object der asynchronen Operation
	 */
	asyncErrorHandler(error) {
		// eslint-disable-next-line no-console
		console.error('Request failed: ', error.message);
		this.hookAfterAsyncErrorHandler(error);
	}

	/**
	 * Hook um bei fehlerhaftem asynchronen Request weitere Funktionalitäten ausführen zu können.
	 *
	 * @param {DOMException} error Error-Object der asynchronen Operation
	 */
	// eslint-disable-next-line no-unused-vars
	hookAfterAsyncErrorHandler(error){}
}
